import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Check,
  Mail,
  MessageCircle,
  Paperclip,
  Search,
  Smile,
  X,
} from "lucide-react";
import EmojiPicker, { Theme, type EmojiClickData } from "emoji-picker-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import {
  inboundSenderEmail,
  inboundSenderPhone,
  type InboxTicketRow,
} from "@/lib/support-views";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const SENDERS = ["info@coliviq.de", "kontakt@coliviq.de"] as const;
const SUPPORT_EMAIL_SIGNATURE =
  "--\ncoliviq team\n\ncoliviq GmbH\nElvirastraße 10\n80636 München\n\ne-mail: kontakt@coliviq.de\nweb: www.coliviq.de";
const SUPPORT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
const SUPPORT_ATTACHMENT_MAX_COUNT = 10;
const SUPPORT_ATTACHMENT_TYPES = new Set([
  "application/pdf",
  "image/gif",
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
]);
type Channel = "whatsapp" | "email";
type Contact = {
  key: string;
  sourceIssueId?: string;
  recipientKind?: "booking" | "lessor";
  recipientId?: string;
  name: string;
  detail: string;
  email: string | null;
  phone: string | null;
  category?: "tenant" | "owner" | "property" | "apartment" | "room";
  members?: Contact[];
  roomOnly?: boolean;
};
type RequestIds = { issueId: string; commentId: string };
type EncodedAttachment = {
  name: string;
  type: string;
  size: number;
  contentBase64: string;
};
type DirectoryBooking = {
  id: number;
  user_id: string | null;
  name: string | null;
  email: string | null;
  phone: string | null;
  room_id: number | null;
  rooms: {
    id: number;
    title: string | null;
    property_id: number | null;
    apartment_id: number | null;
    properties: {
      id: number;
      title: string | null;
      address: string | null;
    } | null;
  } | null;
};
type DirectoryLessor = {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  city_postal: string | null;
};
type DirectoryApartment = {
  id: number;
  label: string | null;
  property_id: number;
};

async function responseMessage(error: unknown, fallback: string) {
  const response = (error as { context?: unknown } | null)?.context;
  if (response instanceof Response) {
    const payload = (await response
      .clone()
      .json()
      .catch(() => null)) as {
      error?: unknown;
    } | null;
    if (typeof payload?.error === "string") return payload.error;
  }
  const message = (error as { message?: unknown } | null)?.message;
  return typeof message === "string" &&
    message !== "Edge Function returned a non-2xx status code"
    ? message
    : fallback;
}

function encodeFile(file: File): Promise<EncodedAttachment> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () =>
      reject(reader.error ?? new Error("File read failed"));
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const comma = result.indexOf(",");
      if (comma < 0) return reject(new Error("File read failed"));
      resolve({
        name: file.name,
        type: file.type,
        size: file.size,
        contentBase64: result.slice(comma + 1),
      });
    };
    reader.readAsDataURL(file);
  });
}

export function NewConversationDialog({
  open,
  onOpenChange,
  tickets,
  onStarted,
  initialSearch = "",
  initialRecipient = null,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tickets: InboxTicketRow[];
  onStarted: (issueId: string) => void;
  initialSearch?: string;
  initialRecipient?: {
    recipientKind: "booking" | "lessor";
    recipientId: string;
  } | null;
}) {
  const { t, language } = useLanguage();
  const [channel, setChannel] = useState<Channel>("whatsapp");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Contact[]>([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [fromAddress, setFromAddress] = useState<(typeof SENDERS)[number]>(
    SENDERS[0]
  );
  const [sending, setSending] = useState(false);
  const [requestIds, setRequestIds] = useState<Record<string, RequestIds>>({});
  const [batchId, setBatchId] = useState<string | null>(null);
  const messageRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: directoryContacts = [] } = useQuery({
    queryKey: ["support-contact-directory", language],
    enabled: open,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Contact[]> => {
      // Some focused component tests intentionally provide only the Edge
      // Function mock. Existing-ticket contacts remain available there.
      if (
        typeof (supabase as unknown as { from?: unknown }).from !== "function"
      ) {
        return [];
      }
      const [bookingsResult, lessorsResult, apartmentsResult] =
        await Promise.all([
          supabase
            .from("bookings")
            .select(
              "id,user_id,name,email,phone,room_id,rooms:room_id(id,title,property_id,apartment_id,properties:property_id(id,title,address))"
            )
            .eq("tenant_active", true)
            .is("admin_archived_at", null)
            .order("name", { ascending: true }),
          supabase
            .from("lessors")
            .select("id,name,email,phone,address,city_postal")
            .order("name", { ascending: true }),
          supabase
            .from("apartments")
            .select("id,label,property_id")
            .is("archived_at", null)
            .order("label", { ascending: true }),
        ]);
      if (bookingsResult.error) throw bookingsResult.error;
      if (lessorsResult.error) throw lessorsResult.error;
      if (apartmentsResult.error) throw apartmentsResult.error;

      const contacts: Contact[] = [];
      const seen = new Set<string>();
      type TenantContact = Contact & {
        propertyId: string | null;
        apartmentId: string | null;
        roomId: string | null;
        propertyName: string;
        roomName: string;
      };
      const tenantContacts: TenantContact[] = [];
      for (const booking of (bookingsResult.data ??
        []) as unknown as DirectoryBooking[]) {
        const email = booking.email?.trim() || null;
        const phone = booking.phone?.trim() || null;
        if (!email && !phone) continue;
        const key = booking.user_id
          ? `user:${booking.user_id}`
          : email
            ? `email:${email.toLowerCase()}`
            : `phone:${phone}`;
        const property =
          booking.rooms?.properties?.address ||
          booking.rooms?.properties?.title ||
          "";
        const tenantContact: TenantContact = {
          key,
          recipientKind: "booking",
          recipientId: String(booking.id),
          name:
            booking.name?.trim() ||
            email ||
            phone ||
            t("support.contact.tenant", "Tenant"),
          detail: [phone, email, property, booking.rooms?.title]
            .filter(Boolean)
            .join(" · "),
          email,
          phone,
          category: "tenant",
          propertyId: booking.rooms?.property_id
            ? String(booking.rooms.property_id)
            : null,
          apartmentId: booking.rooms?.apartment_id
            ? String(booking.rooms.apartment_id)
            : null,
          roomId: booking.room_id ? String(booking.room_id) : null,
          propertyName: property,
          roomName: booking.rooms?.title || "",
        };
        tenantContacts.push(tenantContact);
        if (!seen.has(key)) {
          seen.add(key);
          contacts.push(tenantContact);
        }
      }
      for (const lessor of (lessorsResult.data ?? []) as DirectoryLessor[]) {
        const email = lessor.email?.trim() || null;
        const phone = lessor.phone?.trim() || null;
        if (!email && !phone) continue;
        const key = `owner:${lessor.id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        contacts.push({
          key,
          recipientKind: "lessor",
          recipientId: String(lessor.id),
          name:
            lessor.name?.trim() ||
            email ||
            phone ||
            t("support.contact.owner", "Owner"),
          detail: [phone, email, lessor.address, lessor.city_postal]
            .filter(Boolean)
            .join(" · "),
          email,
          phone,
          category: "owner",
        });
      }

      const propertyGroups = new Map<
        string,
        { name: string; members: TenantContact[] }
      >();
      const apartmentGroups = new Map<string, TenantContact[]>();
      const roomGroups = new Map<string, TenantContact[]>();
      for (const tenant of tenantContacts) {
        if (tenant.propertyId) {
          const group = propertyGroups.get(tenant.propertyId) ?? {
            name: tenant.propertyName || t("support.new.property", "Property"),
            members: [],
          };
          group.members.push(tenant);
          propertyGroups.set(tenant.propertyId, group);
        }
        if (tenant.apartmentId) {
          const members = apartmentGroups.get(tenant.apartmentId) ?? [];
          members.push(tenant);
          apartmentGroups.set(tenant.apartmentId, members);
        }
        if (tenant.roomId) {
          const members = roomGroups.get(tenant.roomId) ?? [];
          members.push(tenant);
          roomGroups.set(tenant.roomId, members);
        }
      }
      const groupOptions: Contact[] = [];
      for (const [propertyId, group] of propertyGroups) {
        groupOptions.push({
          key: `property:${propertyId}`,
          name: group.name,
          detail: `${t("support.new.property", "Property")} · ${t("support.new.tenantCount", "{count} tenants").replace("{count}", String(group.members.length))}`,
          email: null,
          phone: null,
          category: "property",
          members: group.members,
        });
      }
      const apartmentById = new Map(
        ((apartmentsResult.data ?? []) as DirectoryApartment[]).map((row) => [
          String(row.id),
          row,
        ])
      );
      for (const [apartmentId, members] of apartmentGroups) {
        const apartment = apartmentById.get(apartmentId);
        const propertyName = members[0]?.propertyName || "";
        groupOptions.push({
          key: `apartment:${apartmentId}`,
          name: [propertyName, apartment?.label].filter(Boolean).join(" · "),
          detail: `${t("support.new.apartment", "Apartment")} · ${t("support.new.tenantCount", "{count} tenants").replace("{count}", String(members.length))}`,
          email: null,
          phone: null,
          category: "apartment",
          members,
        });
      }
      for (const [roomId, members] of roomGroups) {
        const tenant = members[0];
        groupOptions.push({
          key: `room:${roomId}`,
          name: [tenant.propertyName, tenant.roomName]
            .filter(Boolean)
            .join(" · "),
          detail: `${t("support.new.room", "Room")} · ${members.map((m) => m.name).join(", ")}`,
          email: null,
          phone: null,
          category: "room",
          members,
          roomOnly: true,
        });
      }
      return [...groupOptions, ...contacts];
    },
  });

  useEffect(() => {
    if (!open) return;
    setChannel("whatsapp");
    setQuery(initialSearch);
    setSelected([]);
    setSubject("");
    setMessage("");
    setFiles([]);
    setEmojiOpen(false);
    setFromAddress(SENDERS[0]);
    setRequestIds({});
  }, [initialSearch, initialRecipient, open]);

  const contacts = useMemo(() => {
    const seen = new Set<string>();
    const out: Contact[] = [];
    for (const ticket of tickets) {
      const row = ticket as InboxTicketRow & {
        metadata?: unknown;
        lessor_id?: string | null;
      };
      const email = inboundSenderEmail(row.metadata);
      const phone = inboundSenderPhone(row.metadata);
      const key = ticket.reported_by
        ? `user:${ticket.reported_by}`
        : ticket.contact_type === "owner" && row.lessor_id
          ? `owner:${row.lessor_id}`
          : email
            ? `email:${email}`
            : phone
              ? `phone:${phone}`
              : "";
      if (!key || seen.has(key)) continue;
      seen.add(key);
      const location = [ticket.properties?.title, ticket.rooms?.title]
        .filter(Boolean)
        .join(" · ");
      out.push({
        key,
        sourceIssueId: ticket.id,
        name: ticket.tenantName,
        detail: [phone ? `+${phone}` : null, email, location]
          .filter(Boolean)
          .join(" · "),
        email,
        phone,
      });
    }
    for (const contact of directoryContacts) {
      if (seen.has(contact.key)) continue;
      seen.add(contact.key);
      out.push(contact);
    }
    return out;
  }, [directoryContacts, tickets]);

  useEffect(() => {
    if (!open || !initialRecipient || selected.length) return;
    const contact = contacts.find(
      (candidate) =>
        candidate.recipientKind === initialRecipient.recipientKind &&
        candidate.recipientId === initialRecipient.recipientId
    );
    if (contact) setSelected([contact]);
  }, [contacts, initialRecipient, open, selected.length]);

  const normalizedQuery = query.trim().toLowerCase();
  const filtered = contacts.filter(
    (contact) =>
      (!contact.roomOnly || !!normalizedQuery) &&
      `${contact.name} ${contact.detail}`
        .toLowerCase()
        .includes(normalizedQuery)
  );
  const recipients = useMemo(() => {
    const out: Contact[] = [];
    const seen = new Set<string>();
    for (const option of selected) {
      for (const contact of option.members ?? [option]) {
        if (seen.has(contact.key)) continue;
        seen.add(contact.key);
        out.push(contact);
      }
    }
    return out;
  }, [selected]);
  const isBroadcast =
    selected.length > 1 || selected.some((contact) => !!contact.members);
  const dirty = () => {
    setRequestIds({});
    setBatchId(null);
  };
  const toggleContact = (contact: Contact) => {
    setSelected((current) =>
      current.some((item) => item.key === contact.key)
        ? current.filter((item) => item.key !== contact.key)
        : [...current, contact]
    );
    dirty();
  };

  const send = async () => {
    if (
      !recipients.length ||
      (!message.trim() && !files.length) ||
      (channel === "email" && !subject.trim())
    )
      return;
    let attachments: EncodedAttachment[];
    try {
      attachments = await Promise.all(files.map(encodeFile));
    } catch {
      toast.error(
        t("support.new.fileReadFailed", "An attachment could not be read.")
      );
      return;
    }
    const ids = { ...requestIds };
    for (const contact of recipients) {
      ids[contact.key] ??= {
        issueId: crypto.randomUUID(),
        commentId: crypto.randomUUID(),
      };
    }
    setRequestIds(ids);
    const activeBatchId = isBroadcast ? (batchId ?? crypto.randomUUID()) : null;
    if (activeBatchId !== batchId) setBatchId(activeBatchId);
    setSending(true);
    const results = await Promise.all(
      recipients.map(async (contact, index) => {
        const { data, error } = await supabase.functions.invoke<{
          issueId?: string;
          sent?: boolean;
          error?: string;
        }>("start-support-conversation", {
          body: {
            ...ids[contact.key],
            ...(contact.sourceIssueId
              ? { sourceIssueId: contact.sourceIssueId }
              : {
                  recipientKind: contact.recipientKind,
                  recipientId: contact.recipientId,
                }),
            channel,
            subject: subject.trim(),
            message: message.trim(),
            attachments,
            fromAddress,
            ...(activeBatchId
              ? {
                  batchId: activeBatchId,
                  batchRecipientLabel: contact.name,
                  batchRecipientCount: recipients.length,
                  batchPosition: index,
                }
              : {}),
          },
        });
        return { contact, data, error };
      })
    );
    setSending(false);
    const failures = results.filter(
      ({ data, error }) => error || !data?.sent || !data.issueId
    );
    if (failures.length) {
      const first = failures[0];
      toast.error(
        first.data?.error ||
          (await responseMessage(
            first.error,
            t(
              "support.new.sendFailed",
              "{count} message(s) could not be sent."
            ).replace("{count}", String(failures.length))
          ))
      );
      return;
    }
    toast.success(
      t("support.new.sent", "{count} message(s) sent.").replace(
        "{count}",
        String(results.length)
      )
    );
    onOpenChange(false);
    onStarted(results[0].data!.issueId!);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !sending && onOpenChange(next)}>
      <DialogContent className="support-new-message min-w-0 max-w-2xl gap-0 overflow-hidden rounded-2xl p-0">
        <DialogHeader className="min-w-0 border-b border-[#eceae5] px-7 py-6">
          <DialogTitle>{t("support.new.title", "New message")}</DialogTitle>
          <DialogDescription>
            {t(
              "support.new.description",
              "Start a new conversation with one or more contacts."
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="min-w-0 space-y-5 px-7 py-6">
          <div
            className="inline-flex rounded-full bg-[#f1f0ec] p-1"
            role="group"
            aria-label={t("support.new.channel", "Channel")}
          >
            {(["whatsapp", "email"] as Channel[]).map((value) => (
              <button
                key={value}
                type="button"
                className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${
                  channel === value
                    ? "bg-white text-[#092e2c] shadow-sm"
                    : "text-[#7a8e8c]"
                }`}
                onClick={() => {
                  setChannel(value);
                  dirty();
                }}
              >
                {value === "whatsapp" ? (
                  <MessageCircle size={16} />
                ) : (
                  <Mail size={16} />
                )}
                {value === "whatsapp"
                  ? "WhatsApp"
                  : t("support.channel.email", "Email")}
              </button>
            ))}
          </div>
          {isBroadcast ? (
            <div className="flex flex-wrap items-center gap-2 text-xs text-[#47605e]">
              <span className="rounded-full bg-[#dff3ef] px-3 py-1 font-semibold text-[#0f4c4a]">
                {t("support.new.broadcast", "Broadcast")}
              </span>
              <span>
                {t(
                  "support.new.broadcastRecipients",
                  "{count} recipients · each person receives the message individually"
                ).replace("{count}", String(recipients.length))}
              </span>
            </div>
          ) : null}

          <div>
            <label
              htmlFor="support-contact-search"
              className="mb-2 block text-xs font-semibold uppercase tracking-[0.1em] text-[#9aa6a4]"
            >
              {t("support.new.recipients", "Recipients")}
            </label>
            {selected.length ? (
              <div className="mb-2 flex flex-wrap gap-2">
                {selected.map((contact) => (
                  <button
                    key={contact.key}
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full bg-[#edf4f2] px-3 py-1.5 text-sm font-medium text-[#092e2c]"
                    onClick={() => toggleContact(contact)}
                  >
                    {contact.name}
                    <X size={13} />
                  </button>
                ))}
              </div>
            ) : null}
            <div className="min-w-0 overflow-hidden rounded-xl border border-[#dfe5e1]">
              <div className="flex items-center gap-3 border-b border-[#eceae5] px-4">
                <Search size={17} className="text-[#9aa6a4]" />
                <input
                  id="support-contact-search"
                  autoFocus
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  className="h-12 w-full bg-transparent text-sm outline-none"
                  placeholder={t(
                    "support.new.searchContacts",
                    "Name, room, apartment, property, email or phone"
                  )}
                />
              </div>
              <div className="min-w-0 max-h-48 overflow-y-auto overflow-x-hidden p-1">
                {filtered.length ? (
                  filtered.map((contact) => {
                    const checked = selected.some(
                      (item) => item.key === contact.key
                    );
                    return (
                      <button
                        key={contact.key}
                        type="button"
                        className="flex w-full max-w-full items-center gap-3 overflow-hidden rounded-lg px-3 py-2.5 text-left hover:bg-[#f8f7f3]"
                        onClick={() => toggleContact(contact)}
                      >
                        <span
                          className={`grid h-6 w-6 shrink-0 place-items-center rounded-md border ${
                            checked
                              ? "border-[#092e2c] bg-[#092e2c] text-white"
                              : "border-[#cad4d1]"
                          }`}
                        >
                          {checked ? <Check size={14} /> : null}
                        </span>
                        <span className="min-w-0">
                          <strong className="block truncate text-sm">
                            {contact.name}
                          </strong>
                          <small className="block truncate text-[#7a8e8c]">
                            {contact.detail}
                          </small>
                        </span>
                        <span className="ml-auto shrink-0 text-[11px] font-semibold text-[#47605e]">
                          {contact.category === "property"
                            ? t("support.new.property", "Property")
                            : contact.category === "apartment"
                              ? t("support.new.apartment", "Apartment")
                              : contact.category === "room"
                                ? t("support.new.room", "Room")
                                : contact.category === "owner"
                                  ? t("support.contact.owner", "Owner")
                                  : t("support.contact.tenant", "Tenant")}
                        </span>
                      </button>
                    );
                  })
                ) : (
                  <p className="px-3 py-8 text-center text-sm text-[#7a8e8c]">
                    {t("support.new.noContact", "No contact found.")}
                  </p>
                )}
              </div>
            </div>
          </div>

          {channel === "email" ? (
            <div className="grid gap-4 sm:grid-cols-[1fr_220px]">
              <div>
                <label
                  htmlFor="support-new-subject"
                  className="mb-2 block text-xs font-semibold uppercase tracking-[0.1em] text-[#9aa6a4]"
                >
                  {t("support.new.subject", "Subject")}
                </label>
                <input
                  id="support-new-subject"
                  value={subject}
                  maxLength={200}
                  onChange={(event) => {
                    setSubject(event.target.value);
                    dirty();
                  }}
                  className="h-11 w-full rounded-xl border border-[#dfe5e1] px-4 text-sm outline-none"
                />
              </div>
              <div>
                <label
                  htmlFor="support-new-from"
                  className="mb-2 block text-xs font-semibold uppercase tracking-[0.1em] text-[#9aa6a4]"
                >
                  {t("support.new.from", "From")}
                </label>
                <select
                  id="support-new-from"
                  value={fromAddress}
                  onChange={(event) => {
                    setFromAddress(
                      event.target.value as (typeof SENDERS)[number]
                    );
                    dirty();
                  }}
                  className="h-11 w-full rounded-xl border border-[#dfe5e1] bg-white px-3 text-sm"
                >
                  {SENDERS.map((sender) => (
                    <option key={sender} value={sender}>
                      {sender}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : null}
          <div>
            <label
              htmlFor="support-new-message"
              className="mb-2 block text-xs font-semibold uppercase tracking-[0.1em] text-[#9aa6a4]"
            >
              {t("support.new.message", "Message")}
            </label>
            <div className="relative">
              <textarea
                ref={messageRef}
                id="support-new-message"
                value={message}
                maxLength={20_000}
                rows={7}
                onChange={(event) => {
                  setMessage(event.target.value);
                  dirty();
                }}
                className="w-full resize-none rounded-xl border border-[#dfe5e1] px-4 py-3 text-sm outline-none"
                placeholder={t("support.new.writeMessage", "Write a message …")}
              />
              {emojiOpen ? (
                <div className="absolute bottom-12 left-2 z-50 overflow-hidden rounded-2xl border bg-white shadow-xl">
                  <EmojiPicker
                    theme={Theme.LIGHT}
                    height={340}
                    width={320}
                    lazyLoadEmojis
                    previewConfig={{ showPreview: false }}
                    onEmojiClick={(emoji: EmojiClickData) => {
                      const textarea = messageRef.current;
                      const start = textarea?.selectionStart ?? message.length;
                      const end = textarea?.selectionEnd ?? start;
                      setMessage(
                        (current) =>
                          `${current.slice(0, start)}${emoji.emoji}${current.slice(end)}`
                      );
                      setEmojiOpen(false);
                      dirty();
                    }}
                  />
                </div>
              ) : null}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                accept={[...SUPPORT_ATTACHMENT_TYPES].join(",")}
                onChange={(event) => {
                  const selectedFiles = [...(event.target.files ?? [])];
                  let totalBytes = files.reduce(
                    (sum, current) => sum + current.size,
                    0
                  );
                  const fileKeys = new Set(
                    files.map((file) => `${file.name}:${file.size}`)
                  );
                  for (const file of selectedFiles) {
                    if (!SUPPORT_ATTACHMENT_TYPES.has(file.type)) {
                      toast.error(
                        t(
                          "support.new.unsupportedFile",
                          "{name}: unsupported file type"
                        ).replace("{name}", file.name)
                      );
                      continue;
                    }
                    if (file.size > SUPPORT_ATTACHMENT_MAX_BYTES) {
                      toast.error(
                        t(
                          "support.new.fileTooLarge",
                          "{name}: max 10 MB"
                        ).replace("{name}", file.name)
                      );
                      continue;
                    }
                    const fileKey = `${file.name}:${file.size}`;
                    const duplicate = fileKeys.has(fileKey);
                    if (
                      !duplicate &&
                      fileKeys.size >= SUPPORT_ATTACHMENT_MAX_COUNT
                    ) {
                      toast.error(
                        t(
                          "support.new.tooManyFiles",
                          "You can attach up to 10 files."
                        )
                      );
                      continue;
                    }
                    if (
                      !duplicate &&
                      totalBytes + file.size > SUPPORT_ATTACHMENT_MAX_BYTES
                    ) {
                      toast.error(
                        t(
                          "support.new.filesTooLarge",
                          "Attachments must total at most 10 MB."
                        )
                      );
                      continue;
                    }
                    if (!duplicate) {
                      totalBytes += file.size;
                      fileKeys.add(fileKey);
                    }
                    setFiles((current) =>
                      current.some(
                        (item) =>
                          item.name === file.name && item.size === file.size
                      )
                        ? current
                        : [...current, file]
                    );
                  }
                  event.target.value = "";
                  dirty();
                }}
              />
              <button
                type="button"
                className="inline-flex h-9 items-center gap-2 rounded-full border border-[#dfe5e1] px-3 text-sm text-[#47605e]"
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip size={15} />
                {t("support.new.attach", "Attach")}
              </button>
              <button
                type="button"
                aria-label={t("support.new.emoji", "Add emoji")}
                aria-expanded={emojiOpen}
                className="grid h-9 w-9 place-items-center rounded-full border border-[#dfe5e1] text-[#47605e]"
                onClick={() => setEmojiOpen((current) => !current)}
              >
                <Smile size={16} />
              </button>
              {files.map((file) => (
                <span
                  key={`${file.name}:${file.size}`}
                  className="inline-flex max-w-full items-center gap-2 rounded-full bg-[#edf4f2] px-3 py-1.5 text-xs text-[#092e2c]"
                >
                  <span className="max-w-48 truncate">{file.name}</span>
                  <button
                    type="button"
                    aria-label={t(
                      "support.new.removeAttachment",
                      "Remove {name}"
                    ).replace("{name}", file.name)}
                    onClick={() => {
                      setFiles((current) =>
                        current.filter((item) => item !== file)
                      );
                      dirty();
                    }}
                  >
                    <X size={13} />
                  </button>
                </span>
              ))}
            </div>
          </div>
          <p className="text-xs text-[#7a8e8c]">
            {t("support.composer.via", "via")}{" "}
            {channel === "whatsapp"
              ? "WhatsApp"
              : t("support.channel.email", "Email")}
            {" · "}
            {t("support.new.recipientCount", "{count} recipient(s)").replace(
              "{count}",
              String(recipients.length)
            )}
          </p>
          {channel === "email" ? (
            <details className="text-xs text-[#7a8e8c]">
              <summary className="cursor-pointer font-medium">
                {t("support.composer.signature", "Signature")}
              </summary>
              <span className="mt-2 block whitespace-pre-line">
                {SUPPORT_EMAIL_SIGNATURE}
              </span>
            </details>
          ) : null}
        </div>

        <DialogFooter className="min-w-0 items-center gap-3 border-t border-[#eceae5] px-7 py-5 sm:justify-end sm:space-x-0">
          <button
            type="button"
            className="support-primary flex-none"
            disabled={
              sending ||
              !recipients.length ||
              (!message.trim() && !files.length) ||
              (channel === "email" && !subject.trim())
            }
            onClick={send}
          >
            {sending
              ? t("support.new.sending", "Sending …")
              : t("common.send", "Send")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
